/* eslint-disable @typescript-eslint/no-explicit-any */
import { isString, noop } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import type { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router';

import type { NavigationDirections, State, StateProp, WizardProp } from '@swf/core/types/Wizard';
import { Event } from '@swf/core/types/Wizard';

import { getMeta } from '../../Machine';

export interface WizardNavigationValue extends NavigationDirections {
  trigger?: Event;
  goNext(values?: any): void;
  goPrevious(): void;
  goJump(): void;
}

interface Props
  extends Partial<WizardProp>,
    Partial<StateProp>,
    RouteComponentProps,
    React.PropsWithChildren {
  bootstrap?: boolean;
}

export enum JumpDirection {
  Next = 'next',
  Previous = 'previous'
}

export const Context = React.createContext<WizardNavigationValue>({
  goNext: noop,
  goPrevious: noop,
  goJump: noop
});

const getUrl = (value: any, path = ''): string => {
  if (isString(value)) {
    return `${path}/${value}`;
  }

  const key: any = Object.keys(value).pop();

  return getUrl(value[key], `${path}/${key}`);
};

export const getInitial = (state?: State) =>
  state
    ? {
        initialEntries: [getUrl(state.value)],
        initialIndex: 0
      }
    : {};

const getDirections = (state?: State) => {
  if (!state) {
    return {};
  }

  const { nextEvents, event, context } = state;
  if (event.type === Event.JUMP) {
    return {
      next: context.data.goJump.direction === JumpDirection.Next,
      previous: context.data.goJump.direction === JumpDirection.Previous
    };
  }

  return {
    next: nextEvents.indexOf(Event.NEXT) >= 0,
    previous: nextEvents.indexOf(Event.PREVIOUS) >= 0
  };
};

const Navigation: React.FC<Props> = ({ history, children, wizard, state, bootstrap }) => {
  const [directions, setDirections] = useState<NavigationDirections>(getDirections(state));
  const [trigger, setTrigger] = useState<Event>();

  useEffect(() => {
    if (!wizard) {
      return;
    }

    const transitionHandler = (currentState: State) => {
      const {
        meta,
        value,
        event: { type }
      } = currentState;
      const { navigate } = getMeta(meta) as Record<string, boolean>;

      if (type in Event) {
        setTrigger(type as Event);
      }

      if (navigate) {
        setDirections(getDirections(currentState));
        history.push(getUrl(value));
      }
    };

    wizard.onTransition(transitionHandler);
    if (bootstrap) {
      wizard.send(Event.NEXT);
    }

    return () => {
      wizard.off(transitionHandler);
    };
  }, [history, wizard, bootstrap]);

  return (
    <Context.Provider
      value={{
        ...directions,
        trigger,

        goNext: useCallback((data: any) => wizard && wizard.send(Event.NEXT, { data }), [wizard]),

        goPrevious: useCallback(() => wizard && wizard.send(Event.PREVIOUS), [wizard]),

        goJump: useCallback(() => wizard && wizard.send(Event.JUMP), [wizard])
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default withRouter(Navigation);
